import { Accordion } from "@/components/ui/accordion";
import { ROUTES } from "@/constants/routes";
import { useCanvasPermissions } from "@/pages/canvas/hooks/useCanvasPermissions";
import { Frame, MessagesSquare } from "lucide-react";
import { useCallback, useState } from "react";
import { useMatch } from "react-router-dom";
import { RailGroup } from "../rail/RailGroup";
import { RailGroupActionButton } from "../rail/RailGroupActionButton";
import { RailZeroState } from "../rail/RailZeroState";
import { CanvasRailChats } from "./CanvasRailChats";
import { CanvasRailContents } from "./CanvasRailContents";
import { CanvasRailContentsControls } from "./CanvasRailContentsControls";
import { CanvasRailOsDropZone } from "./CanvasRailOsDropZone";
import { CanvasRailSearch } from "./CanvasRailSearch";
import {
  anchorCenterFromElement,
  dispatchCanvasToggleAddContext,
} from "./canvasRailAddContextEvents";
import { canvasRailCopy } from "./canvasRailCopy";
import {
  dispatchClearExplorerSelection,
  targetIsExplorerRow,
  useClearExplorerSelectionListener,
} from "./canvasRailSelectionEvents";
import { useCanvasRailContents } from "./useCanvasRailContents";
import { useCanvasRailNewChat } from "./useCanvasRailNewChat";

export function CanvasRail() {
  const canvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const canvasId = canvasMatch?.params?.canvasId ?? "";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set(),
  );

  const { canEdit } = useCanvasPermissions(canvasId || undefined);
  const contents = useCanvasRailContents(canvasId, searchQuery);
  const { handleNewChat, isCreatingChat } = useCanvasRailNewChat(
    canvasId,
    canEdit,
  );

  const clearLocalSelections = useCallback(() => {
    setSelectedFileIds((prev) => (prev.size > 0 ? new Set() : prev));
    setSelectedNodeIds((prev) => (prev.size > 0 ? new Set() : prev));
  }, []);
  useClearExplorerSelectionListener(clearLocalSelections);

  const handlePaneBackgroundClick = useCallback((e: React.MouseEvent) => {
    if (targetIsExplorerRow(e.target)) return;
    dispatchClearExplorerSelection();
  }, []);

  const handleAddContext = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      dispatchCanvasToggleAddContext(
        canvasId,
        anchorCenterFromElement(e.currentTarget),
      );
    },
    [canvasId],
  );

  if (!canvasId) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          {canvasRailCopy.noCanvas.description}
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        onClick={handlePaneBackgroundClick}
      >
        <CanvasRailSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <CanvasRailOsDropZone
          onFiles={null}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2"
          data-onboarding-target="canvas-explorer-contents"
        >
          <Accordion
            type="multiple"
            defaultValue={["canvas-chats", "canvas-contents"]}
            className="flex flex-col"
          >
            <RailGroup
              groupId="canvas-chats"
              label="Canvas Chats"
              icon={MessagesSquare}
              noCap
              rightSlot={
                canEdit ? (
                  <RailGroupActionButton
                    label="New chat"
                    onClick={() => void handleNewChat()}
                    disabled={isCreatingChat}
                  />
                ) : null
              }
              items={[
                <CanvasRailChats
                  key="canvas-chats-body"
                  canvasId={canvasId}
                  searchQuery={searchQuery}
                  canEdit={canEdit}
                />,
              ]}
            />

            <RailGroup
              groupId="canvas-contents"
              label="Canvas Contents"
              icon={Frame}
              noCap
              rightSlot={
                <CanvasRailContentsControls
                  canvasId={canvasId}
                  canEdit={canEdit}
                  optionalFilters={contents.optionalFilters}
                  onToggleOptionalFilter={contents.toggleOptionalFilter}
                  viewMode={contents.activeViewMode}
                  onChangeViewMode={(next) => contents.setViewMode(next)}
                />
              }
              items={
                contents.isContentsLoading || contents.isEmpty
                  ? []
                  : [
                      <CanvasRailContents
                        key="canvas-contents-body"
                        canvasId={canvasId}
                        canEdit={canEdit}
                        contents={contents}
                        selectedNodeIds={selectedNodeIds}
                        onSelectionChange={setSelectedNodeIds}
                        selectedFileIds={selectedFileIds}
                        onFileSelectionChange={setSelectedFileIds}
                      />,
                    ]
              }
              emptyState={
                contents.isContentsLoading ? undefined : (
                  <RailZeroState
                    icon={Frame}
                    accent="teal"
                    title={canvasRailCopy.empty.title}
                    description={canvasRailCopy.empty.description}
                    variant={canEdit ? "cta" : "passive"}
                    action={
                      canEdit
                        ? {
                            label: canvasRailCopy.empty.cta,
                            onClick: handleAddContext,
                          }
                        : undefined
                    }
                  />
                )
              }
            />
          </Accordion>
        </CanvasRailOsDropZone>
      </div>
    </>
  );
}
